import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  ACTIVATE_USER,
  ASSIGN_USER_TO_PROJECT,
  CREATE_USER,
  DEACTIVATE_USER,
  GET_COMPANIES,
  GET_USER_ROLES,
  GET_USERS,
  UPDATE_USER,
  get_request_config
} from '@/utils/system_constants/constant.js';

const parse_api_data = (payload) => {
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }

  return payload || {};
};

const get_user_name = (user = {}) => {
  const full_name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return full_name || user.email || 'Unnamed User';
};

const map_user = (profile = {}) => {
  const user = profile.user || {};
  const role = user.role || {};
  return {
    id: profile.user_id || user.id,
    profile_id: profile.profile_id,
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    name: get_user_name(user),
    email: user.email || '',
    role: role.role || role || 'User',
    role_id: role.id || user.role_id || null,
    phone_number: profile.phone_number || '',
    is_active: profile.is_active ?? user.is_active,
  };
};

const is_valid_email = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
const is_valid_phone_number = (phone_number) => /^\d{10}$/.test(String(phone_number || '').trim());
const has_valid_name = (name) => /^[A-Za-z][A-Za-z\s'-]{1,49}$/.test(String(name || '').trim());

export default {
  name: 'UserManagement',
  mixins: [alert_mixin],
  data: () => ({
    users: [],
    roles: [],
    projects: [],
    is_loading: false,
    roles_loading: false,
    projects_loading: false,
    assigned_projects_loading: false,
    creating_user: false,
    updating_user: false,
    assigning_user: false,
    create_form: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      role: ''
    },
    edit_form: {
      user_id: null,
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      role_id: ''
    },
    assign_form: {
      user: null,
      project_ids: [],
      original_project_ids: [],
      project_search: ''
    },
    show_project_dropdown: false,
    pagination: null
  }),
  mounted() {
    this.fetch_users();
    this.fetch_roles();
  },
  computed: {
    active_users_count() {
      return this.users.filter((user) => user.is_active !== false).length;
    },
    inactive_users_count() {
      return this.users.filter((user) => user.is_active === false).length;
    },
    filtered_projects() {
      const search = String(this.assign_form.project_search || '').toLowerCase().trim();
      if (!search) return this.projects;

      return this.projects.filter((project) =>
        this.project_display_name(project).toLowerCase().includes(search)
      );
    },
    selected_project_name() {
      const selected_ids = Array.isArray(this.assign_form.project_ids)
        ? this.assign_form.project_ids
        : [];

      if (!selected_ids.length) return 'Select Projects';

      if (selected_ids.length === 1) {
        const selected_project = this.projects.find(
          (project) => String(project.id) === String(selected_ids[0])
        );
        return selected_project ? this.project_display_name(selected_project) : '1 project selected';
      }

      return `${selected_ids.length} projects selected`;
    }
  },
  methods: {
    parse_error_message(error, fallback) {
      const response_data = parse_api_data(error?.response?.data);
      return (
        response_data.message ||
        response_data.data ||
        error.message ||
        fallback
      );
    },

    reset_create_form() {
      this.create_form = {
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        role: this.roles[0]?.role || ''
      };
    },

    async fetch_roles() {
      this.roles_loading = true;

      try {
        const response = await axios.get(GET_USER_ROLES, get_request_config());
        const response_data = parse_api_data(response.data);
        const roles = Array.isArray(response_data.data) ? response_data.data : [];
        this.roles = roles.filter((role) => role?.role);

        if (!this.create_form.role && this.roles.length) {
          this.create_form.role = this.roles[0].role;
        }
      } catch (error) {
        this.roles = [];
      } finally {
        this.roles_loading = false;
      }
    },

    async fetch_projects() {
      this.projects_loading = true;

      try {
        const response = await axios.get(GET_COMPANIES, get_request_config());
        const response_data = parse_api_data(response.data);
        const items = response_data.data || response_data.projects || [];
        this.projects = Array.isArray(items) ? items : [];
      } catch (error) {
        this.projects = [];
      } finally {
        this.projects_loading = false;
      }
    },

    async fetch_assigned_project_ids(user_id) {
      if (!user_id) return [];

      if (!this.projects.length) {
        await this.fetch_projects();
      }

      this.assigned_projects_loading = true;

      try {
        const responses = await Promise.allSettled(
          this.projects.map((project) =>
            axios.get(
              GET_USERS,
              get_request_config({
                params: {
                  project_id: project.id,
                  page: 1,
                  page_size: 100
                }
              })
            ).then((response) => ({ project, response }))
          )
        );

        return responses
          .filter((result) => result.status === 'fulfilled')
          .filter(({ value }) => {
            const { response } = value;
            const response_data = parse_api_data(response.data);
            const users = Array.isArray(response_data.data) ? response_data.data : [];

            return users.some((user) =>
              String(user.user_id || user.id) === String(user_id)
            );
          })
          .map(({ value }) => String(value.project.id));
      } catch (error) {
        return [];
      } finally {
        this.assigned_projects_loading = false;
      }
    },

    open_create_user_modal() {
      if (!this.roles.length) {
        this.fetch_roles();
      }
    },

    open_edit_user_modal(user) {
      this.edit_form = {
        user_id: user.id,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        role_id: user.role_id || ''
      };

      if (!this.roles.length) {
        this.fetch_roles();
      }
    },

    async open_assign_user_modal(user) {
      if (user.is_active === false) {
        this.show_warning(
          'User Deactivated',
          `You cannot assign ${user.name} because this user is deactivated. Please activate the user first.`
        );
        return;
      }

      this.assign_form = {
        user,
        project_ids: [],
        original_project_ids: [],
        project_search: ''
      };
      this.show_project_dropdown = false;

      if (!this.projects.length) {
        await this.fetch_projects();
      }

      const assigned_project_ids = await this.fetch_assigned_project_ids(user.id);
      this.assign_form.project_ids = assigned_project_ids;
      this.assign_form.original_project_ids = assigned_project_ids;
    },

    project_display_name(project) {
      return project?.project_name || project?.name || `Project ${project?.id || ''}`;
    },

    toggle_project_dropdown() {
      if (this.projects_loading || this.assigned_projects_loading) return;
      this.show_project_dropdown = !this.show_project_dropdown;
    },

    select_project(project) {
      const project_id = String(project?.id || '');
      if (!project_id) return;

      const selected_ids = new Set(
        Array.isArray(this.assign_form.project_ids)
          ? this.assign_form.project_ids.map(String)
          : []
      );

      if (selected_ids.has(project_id)) {
        selected_ids.delete(project_id);
      } else {
        selected_ids.add(project_id);
      }

      this.assign_form.project_ids = Array.from(selected_ids);
    },

    is_project_selected(project_id) {
      return (Array.isArray(this.assign_form.project_ids) ? this.assign_form.project_ids : [])
        .map(String)
        .includes(String(project_id));
    },

    sanitize_user_name_field(form_name, field_name) {
      const form = this[form_name];
      if (!form) return;

      form[field_name] = String(form[field_name] || '')
        .replace(/[^A-Za-z\s'-]/g, '')
        .slice(0, 50);
    },

    sanitize_phone_field(form_name) {
      const form = this[form_name];
      if (!form) return;

      form.phone_number = String(form.phone_number || '')
        .replace(/\D/g, '')
        .slice(0, 10);
    },

    validate_create_user() {
      if (!this.create_form.first_name.trim()) return 'First name is required.';
      if (!has_valid_name(this.create_form.first_name)) return 'First name must be at least 2 letters and cannot contain numbers.';
      if (!this.create_form.last_name.trim()) return 'Last name is required.';
      if (!has_valid_name(this.create_form.last_name)) return 'Last name must be at least 2 letters and cannot contain numbers.';
      if (!this.create_form.email.trim()) return 'Email is required.';
      if (!is_valid_email(this.create_form.email)) return 'Please enter a valid email address.';
      if (!this.create_form.phone_number.trim()) return 'Phone number is required.';
      if (!is_valid_phone_number(this.create_form.phone_number)) return 'Phone number must be exactly 10 digits.';
      if (!this.create_form.role) return 'Role is required.';
      return '';
    },

    async create_user() {
      const validation_message = this.validate_create_user();
      if (validation_message) {
        this.show_error(validation_message);
        return;
      }

      this.creating_user = true;
      const loader = await this.show_loader('Creating user...');

      try {
        const payload = {
          first_name: this.create_form.first_name.trim(),
          last_name: this.create_form.last_name.trim(),
          email: this.create_form.email.trim(),
          phone_number: this.create_form.phone_number.trim(),
          role: this.create_form.role
        };

        const response = await axios.post(CREATE_USER, payload, get_request_config());
        const response_data = parse_api_data(response.data);

        if (response_data?.status && response_data.status !== 'success') {
          throw new Error(response_data.message || 'User creation failed.');
        }

        this.close_modal('create_user_modal');
        this.reset_create_form();
        await this.fetch_users();
        this.show_success(response_data.message || 'User created successfully.');
      } catch (error) {
        const response_data = parse_api_data(error?.response?.data);
        this.show_error(
          response_data.message ||
          response_data.data ||
          error.message ||
          'Failed to create user.'
        );
      } finally {
        this.creating_user = false;
        this.close_loader(loader);
      }
    },

    validate_edit_user() {
      if (!this.edit_form.first_name.trim()) return 'First name is required.';
      if (!has_valid_name(this.edit_form.first_name)) return 'First name must be at least 2 letters and cannot contain numbers.';
      if (!this.edit_form.last_name.trim()) return 'Last name is required.';
      if (!has_valid_name(this.edit_form.last_name)) return 'Last name must be at least 2 letters and cannot contain numbers.';
      if (!this.edit_form.email.trim()) return 'Email is required.';
      if (!is_valid_email(this.edit_form.email)) return 'Please enter a valid email address.';
      if (!this.edit_form.phone_number.trim()) return 'Phone number is required.';
      if (!is_valid_phone_number(this.edit_form.phone_number)) return 'Phone number must be exactly 10 digits.';
      if (!this.edit_form.role_id) return 'Role is required.';
      return '';
    },

    async update_user() {
      const validation_message = this.validate_edit_user();
      if (validation_message) {
        this.show_error(validation_message);
        return;
      }

      this.updating_user = true;
      const loader = await this.show_loader('Updating user...');

      try {
        const payload = {
          user_id: this.edit_form.user_id,
          first_name: this.edit_form.first_name.trim(),
          last_name: this.edit_form.last_name.trim(),
          email: this.edit_form.email.trim(),
          phone_number: this.edit_form.phone_number.trim(),
          role_id: Number(this.edit_form.role_id)
        };

        const response = await axios.post(UPDATE_USER, payload, get_request_config());
        const response_data = parse_api_data(response.data);

        if (response_data?.status && response_data.status !== 'success') {
          throw new Error(response_data.message || 'User update failed.');
        }

        this.close_modal('edit_user_modal');
        await this.fetch_users();
        this.show_success(response_data.message || 'User updated successfully.');
      } catch (error) {
        this.show_error(this.parse_error_message(error, 'Failed to update user.'));
      } finally {
        this.updating_user = false;
        this.close_loader(loader);
      }
    },

    toggle_user_status(user) {
      const should_activate = user.is_active === false;
      const action_label = should_activate ? 'activate' : 'deactivate';

      this.show_confirm(
        `${should_activate ? 'Activate' : 'Deactivate'} User`,
        `Are you sure you want to ${action_label} ${user.name}?`,
        async () => {
          const loader = await this.show_loader(
            `${should_activate ? 'Activating' : 'Deactivating'} user...`
          );

          try {
            const response = await axios.post(
              should_activate ? ACTIVATE_USER : DEACTIVATE_USER,
              { user_id: user.id },
              get_request_config()
            );
            const response_data = parse_api_data(response.data);

            if (response_data?.status && response_data.status !== 'success') {
              throw new Error(response_data.message || 'User status update failed.');
            }

            await this.fetch_users();
            this.show_success(response_data.message || 'User status updated.');
          } catch (error) {
            this.show_error(this.parse_error_message(error, 'Failed to update user status.'));
          } finally {
            this.close_loader(loader);
          }
        }
      );
    },

    async assign_user_to_project() {
      if (!this.assign_form.user?.id) {
        this.show_error('Please select a user.');
        return;
      }

      const selected_project_ids = Array.isArray(this.assign_form.project_ids)
        ? this.assign_form.project_ids
        : [];
      const original_project_ids = Array.isArray(this.assign_form.original_project_ids)
        ? this.assign_form.original_project_ids
        : [];

      if (!selected_project_ids.length && !original_project_ids.length) {
        this.show_error('Please select at least one project.');
        return;
      }

      const original_ids = new Set(original_project_ids.map(String));
      const selected_ids = new Set(selected_project_ids.map(String));
      const changed_project_ids = [
        ...selected_project_ids.filter((project_id) => !original_ids.has(String(project_id))),
        ...original_project_ids.filter((project_id) => !selected_ids.has(String(project_id)))
      ];

      if (!changed_project_ids.length) {
        this.close_modal('assign_user_modal');
        this.assign_form = { user: null, project_ids: [], original_project_ids: [], project_search: '' };
        this.show_project_dropdown = false;
        this.show_success('User project assignments are already up to date.');
        return;
      }

      const assigner_id = this.$store.getters.currentUserId;
      if (!assigner_id) {
        this.show_error('Current user could not be resolved.');
        return;
      }

      this.assigning_user = true;
      const loader = await this.show_loader('Assigning user...');

      try {
        const responses = await Promise.all(
          changed_project_ids.map((project_id) =>
            axios.post(
              ASSIGN_USER_TO_PROJECT,
              {
                project: Number(project_id),
                assigner: Number(assigner_id),
                assignee: Number(this.assign_form.user.id)
              },
              get_request_config()
            )
          )
        );

        responses.forEach((response) => {
          const response_data = parse_api_data(response.data);
          if (response_data?.status && response_data.status !== 'success') {
            throw new Error(response_data.message || 'Project assignment failed.');
          }
        });

        this.close_modal('assign_user_modal');
        this.assign_form = { user: null, project_ids: [], original_project_ids: [], project_search: '' };
        this.show_project_dropdown = false;
        this.show_success(
          `User assignment updated for ${changed_project_ids.length} project${changed_project_ids.length === 1 ? '' : 's'}.`
        );
      } catch (error) {
        this.show_error(this.parse_error_message(error, 'Failed to assign user.'));
      } finally {
        this.assigning_user = false;
        this.close_loader(loader);
      }
    },

    async fetch_users() {
      this.is_loading = true;

      try {
        const response = await axios.get(
          GET_USERS,
          get_request_config({
            params: {
              page: 1,
              page_size: 100
            }
          })
        );
        const response_data = parse_api_data(response.data);
        const items = Array.isArray(response_data.data) ? response_data.data : [];
        this.users = items.map(map_user).filter((user) => user.id != null);
        this.pagination = response_data.pagination || null;
      } catch (error) {
        this.users = [];
        this.show_error('Failed to load users.');
      } finally {
        this.is_loading = false;
      }
    }
  }
};
